import React, { useCallback } from 'react';

// Material UI
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreIcon from '@material-ui/icons/Restore';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Tooltip from '@material-ui/core/Tooltip';
import { Box } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArchiveIcon from '@material-ui/icons/Archive';

// Components
import { MEMBER_GROUP_ROLES, MemberListType, ROLE_LABELS, ROLE_SUBSTITUTES, ROLES } from '../../../../_configuration';
import { useMemberColumnListOptions } from './member-list.utils';
import { SimpleTable } from '../../_core/_ui/simple-table.component';
import { HeaderWithTooltip } from '../../_core/_ui/header-with-tooltip.component';
import { DEFAULT_ASSESSMENT_MEMBER_ROLES, ROLES_WITHOUT_DEFAULT_ASSESSMENT_ROLES, USER_TABLE_COLUMN_FLOORS } from './member-list.consts';
import { areAllRolesOn, toggleAllRoles } from './member-list.helpers';
import { IMemberList, IUpdateMemberRolesParams } from './member-list.types';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { Spacer } from '../../_core/_ui/structure.components';
import { mutedTextColor } from '../../../lib/theme';
import { useMemoPerItem } from '../../../lib/hooks/use-memo-per-item';

const memberKey = (member: IMember) => member._id;

interface ISuperUserMemberList extends Omit<IMemberList, 'activeList' | 'setActiveList'> {
  onViewAgeGroups?: (userId: string) => void;
  activeAgeGroupUserId?: string | null;
  assessments: IAssessment[];
  updateMemberRoles: (args: IUpdateMemberRolesParams) => Promise<any>;
  countArchivedMembers: number;
  viewingArchived: boolean;
  setViewingArchived: (viewing: boolean) => void;
}

const TableCheckbox = ({ checked, indeterminate, disabled, onChange }: { checked: boolean; indeterminate?: boolean; disabled?: boolean; onChange: () => void }) => {
  return <Box display="flex" justifyContent="center" alignItems="center">
    <Checkbox checked={checked} indeterminate={indeterminate} disabled={disabled} onChange={onChange} />
  </Box>
}

export const SuperUserMemberList: React.FC<ISuperUserMemberList> = ({
  members,
  assessments,
  toggleRole,
  updateMemberRoles,
  isSavingRoles,
  currMember,
  restoreAccess,
  revokeAccess,
  countArchivedMembers,
  viewingArchived,
  setViewingArchived,
  assessmentId
}) => {
  const { superUserOptions, assessmentOptions } = useMemberColumnListOptions();
  const associationPerms = MEMBER_GROUP_ROLES[MemberListType.SUPER];
  const assessmentPerms = MEMBER_GROUP_ROLES[MemberListType.ASSESSMENT];
  const selectedAssessment = assessments && assessments.length > 0 ? assessments.find(a => a._id === assessmentId) : null;

  // Helper to check if a role should be locked based on higher-level permissions
  const isRoleLocked = useCallback((member: IMember, role: string, isAssociationLevel: boolean = false) => {
    if (role === ROLES.USER_MANAGEMENT && member._id === currMember?._id) return true;
    if (isAssociationLevel) return false;

    // Check if the user has higher-level permissions that would lock this role
    const roleSubstitutes = ROLE_SUBSTITUTES[MemberListType.ASSESSMENT][role];
    if (!roleSubstitutes) return false;

    // Check if the user has association-level (super user) permissions
    if (roleSubstitutes[MemberListType.SUPER]) {
      const requiredSuperRoles = roleSubstitutes[MemberListType.SUPER];
      if (requiredSuperRoles && (member.isAdmin || member.superUserRoles?.some(userRole => requiredSuperRoles.includes(userRole)))) {
        return true;
      }
    }

    return false;
  }, [currMember]);

  // Helper to get the lock reason for a role
  const getLockReason = useCallback((member: IMember, role: string, isAssociationLevel: boolean = false) => {
    if (role === ROLES.USER_MANAGEMENT && member._id === currMember?._id) return "Cannot lock yourself out of user management";
    if (isAssociationLevel) return '';

    const roleSubstitutes = ROLE_SUBSTITUTES[MemberListType.ASSESSMENT][role];
    if (!roleSubstitutes) return '';

    const reasons = [];

    // Check association-level permissions
    if (roleSubstitutes[MemberListType.SUPER]) {
      const requiredSuperRoles = roleSubstitutes[MemberListType.SUPER];
      if (requiredSuperRoles) {
        if (member.isAdmin) {
          reasons.push(`Admin privileges grant access to all roles`);
        } else {
          const matchingSuperRoles = member.superUserRoles?.filter(userRole => requiredSuperRoles.includes(userRole)) ?? [];
          if (matchingSuperRoles.length > 0) {
            reasons.push(`Association-level ${matchingSuperRoles.map(r => ROLE_LABELS[r] || r).join(', ')}`);
          }
        }
      }
    }

    return reasons.length > 0 ? `This permission is granted by ${reasons.join(' and ')} permission(s)` : '';
  }, [currMember]);

  const headerRows = [
    { cells: [{ borderRight: true }, { content: "Association Level", colSpan: 5 }, { borderLeft: true, content: "Assessment Level", colSpan: 5 },] },
    { cells: [
      { content: "User", borderRight: true },
      { content: "All" },
      ...associationPerms.map(role => ({
        content: <HeaderWithTooltip
          label={superUserOptions.find(o => o.role === role)?.label || role}
          role={role}
        />
      })),
      { content: "All", borderLeft: true },
      ...assessmentPerms.map(role => ({
        content: <HeaderWithTooltip
          label={assessmentOptions.find(o => o.role === role)?.label || role}
          role={role}
        />
      })),
    ]},
  ]

  const rowSavingState = useCallback((member: IMember) => [
    isSavingRoles({ memberId: member._id }),
    selectedAssessment ? isSavingRoles({ memberId: member._id, assessmentId: selectedAssessment._id }) : false,
  ] as const, [isSavingRoles, selectedAssessment]);

  const buildRow = useCallback((member: IMember, [savingAssociation, savingAssessment]: readonly [boolean, boolean]) => {
    const userSuperRoles = member.superUserRoles || [];
    const associationGroup = { roles: userSuperRoles, groupRoles: associationPerms, isLocked: (role: string) => isRoleLocked(member, role, true) };
    const allAssociationChecked = areAllRolesOn(associationGroup);
    const someAssociationChecked = associationPerms.some(role => userSuperRoles.includes(role) || associationGroup.isLocked(role));
    const userAssessmentRoles = member.rolesByAssessment.find(r => r.assessmentId === selectedAssessment?._id)?.roles || [];

    const assessmentGroup = { roles: userAssessmentRoles, groupRoles: assessmentPerms, isLocked: (role: string) => isRoleLocked(member, role) };
    const allAssessmentChecked = areAllRolesOn(assessmentGroup);
    const allAssessmentLocked = assessmentPerms.every(assessmentGroup.isLocked);
    const someAssessmentChecked = assessmentPerms.some(role => userAssessmentRoles.includes(role) || assessmentGroup.isLocked(role));

    return {
      cells: [
        { content: (
          <Box display="flex" flexDirection="column" alignItems="stretch" style={{ gap: '0.25rem', minWidth: 0 }}>
            <Box style={{ overflowWrap: 'anywhere' }}>
              {member.user.name}<br />
              <span style={{ color: mutedTextColor }}>{member.user.email}</span>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end" style={{gap: '0.5rem'}}>
              <IconButton
                size="small"
                onClick={() => {
                  // Trigger selection of this user in age group table
                  const selectEvent = new CustomEvent('selectUserForAgeGroup', {
                    detail: { userId: member._id, userName: member.user.name }
                  });
                  window.dispatchEvent(selectEvent);

                  // Scroll to age group table
                  const ageGroupTable = document.getElementById('age-group-permissions-table');
                  if (ageGroupTable) {
                    ageGroupTable.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                title="View in Age Group Permissions"
              >
                <VisibilityIcon />
              </IconButton>
              {currMember?._id !== member._id && <IconButton
                size="small"
                onClick={() => member.deletedAt ? restoreAccess([member._id]) : revokeAccess([member._id])}
                title={member.deletedAt ? "Restore Access" : "Revoke Access"}
              >
                {member.deletedAt ? <RestoreIcon /> : <DeleteIcon />}
              </IconButton>}
            </Box>
          </Box>
        ), borderRight: true },
        { content:
          <TableCheckbox
            checked={allAssociationChecked}
            indeterminate={!allAssociationChecked && someAssociationChecked}
            disabled={savingAssociation}
            onChange={() => updateMemberRoles({ member, roles: toggleAllRoles(associationGroup) })}
          />
        },
        ...associationPerms.map(role => {
          const isLocked = isRoleLocked(member, role, true);
          const isChecked = userSuperRoles.includes(role) || isLocked;
          const lockReason = getLockReason(member, role, true);

          return { content: (
            <Tooltip title={isLocked ? lockReason : ""} arrow>
              <span>
                <TableCheckbox
                  checked={isChecked}
                  disabled={isLocked || savingAssociation}
                  onChange={() => toggleRole({
                    member,
                    role,
                    forceToggleResult: !userSuperRoles.includes(role) && role === ROLES.CHECK_IN ? false : undefined,
                    forcedOnRoles: !userSuperRoles.includes(role) && role !== ROLES.CHECK_IN ? [] : undefined
                  })}
                />
              </span>
            </Tooltip>
          )}
        }),
        { content: <TableCheckbox
          checked={allAssessmentChecked}
          indeterminate={!allAssessmentChecked && someAssessmentChecked}
          disabled={allAssessmentLocked || !selectedAssessment || savingAssessment}
          onChange={() => {
            // Without an assessment id the write would land on the association-level roles.
            if (!selectedAssessment) return;
            updateMemberRoles({ member, assessmentId: selectedAssessment._id, roles: toggleAllRoles(assessmentGroup) });
          }}
        />, borderLeft: true },
        ...assessmentPerms.map(role => {
          const isLocked = isRoleLocked(member, role);
          const isChecked = userAssessmentRoles.includes(role) || isLocked;
          const lockReason = getLockReason(member, role);

          return { content: (
            <Tooltip title={isLocked ? lockReason : ""} arrow>
              <span>
                <TableCheckbox
                  checked={isChecked}
                  disabled={isLocked || !selectedAssessment || savingAssessment}
                  onChange={() => toggleRole({
                    member,
                    role,
                    forceToggleResult: undefined, // !userAssessmentRoles.includes(role) && role === ROLES.CHECK_IN ? false : undefined, TODO: Double check why we do this
                    forcedOnRoles: !userAssessmentRoles.includes(role) && !ROLES_WITHOUT_DEFAULT_ASSESSMENT_ROLES.includes(role) ? DEFAULT_ASSESSMENT_MEMBER_ROLES : undefined,
                    isAssessmentRole: true
                  })}
                />
              </span>
            </Tooltip>
          )}
        }),
      ],
    }
  }, [associationPerms, assessmentPerms, currMember?._id, selectedAssessment, isRoleLocked, restoreAccess, revokeAccess, updateMemberRoles, getLockReason, toggleRole]);

  // A save or a cache write then rebuilds only the rows it touched.
  const rows = useMemoPerItem(members, memberKey, rowSavingState, buildRow);

  return (
    <>
      <SimpleTable
        headerRows={headerRows}
        rows={rows}
        fitContainer
        stickyHeader
        stickyFirstColumn
        columnMinWidths={USER_TABLE_COLUMN_FLOORS}
        pagination={{
          enabled: true,
          rowsPerPageOptions: [5, 10, 25, 50],
          defaultRowsPerPage: 10
        }}
      />
      <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: "0.5rem" }}>
        {countArchivedMembers > 0 && !viewingArchived && (
          <Box display="flex">
            <Spacer />
            <SecondaryButton
              startIcon={<ArchiveIcon />}
              onClick={() => setViewingArchived(true)}
            >
              View Archived Users
            </SecondaryButton>
          </Box>
        )}
        {viewingArchived && (
          <Box display="flex">
            <Spacer />
            <PrimaryButton
              startIcon={<ArrowBackIosIcon />}
              onClick={() => setViewingArchived(false)}
            >
              Back to Users
            </PrimaryButton>
          </Box>
        )}
      </Box>
    </>
  );
};
